import { Type } from '@angular/core';
import { PrivacyDialogsFastTrackComponent } from '@client/app/modules/data-privacy/dialogs/fast-track/privacy-dialogs-fast-track.component';

export type SideNavigationProfile = 'domains' | 'admin';

export type SideNavigationVisibilityKey =
    | 'prioritized-content'
    | 'accessibility-root'
    | 'accessibility-pdfs'
    | 'accessibility-modern'
    | 'accessibility-legacy'
    | 'accessibility-pdfs-modern'
    | 'accessibility-pdfs-legacy'
    | 'quality-root'
    | 'quality-summary'
    | 'quality-policies'
    | 'quality-pagecorrect'
    | 'quality-pagecorrect-accessibility-fixes'
    | 'quality-spellcheck'
    | 'quality-links'
    | 'quality-readability'
    | 'quality-language'
    | 'insights-root'
    | 'site-performance-root'
    | 'privacy-root'
    | 'statistics-root'
    | 'uptime-root'
    | 'history-center'
    | 'inventory-root'
    | 'seo-root'
    | 'performance-root'
    | 'data-privacy-core'
    | 'consent-manager-root'
    | 'consent-manager-cookie-overview'
    | 'admin-labels'
    | 'admin-audit-log'
    | 'admin-source-code-excludes'
    | 'admin-api-users'
    | 'admin-page-performance'
    | 'admin-jira-connector';

export interface SideNavigationItem {
    id: string;
    label: string;
    icon?: string;
    route?: string;
    routeParams?: Record<string, unknown>;
    dialogParams?: {
        body: Type<unknown>;
        size?: string;
    };
    activeRoutes?: string[];
    routeMatchers?: string[];
    visibilityKey?: SideNavigationVisibilityKey;
    children?: SideNavigationItem[];
    dividerAfter?: boolean;
}

export const DOMAIN_SIDE_NAV_ITEMS: SideNavigationItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'dashboard',
        route: 'base.customer.domain.dashboard',
        activeRoutes: ['base.dashboard', 'base.customer.domain.dashboard'],
    },
    {
        id: 'prioritized-pages',
        label: 'Prioritized Pages',
        icon: 'prioritizedPages',
        route: 'base.customer.domain.pages',
        visibilityKey: 'prioritized-content',
        dividerAfter: true,
    },
    {
        id: 'accessibility',
        label: 'Accessibility',
        icon: 'accessibility',
        route: 'base.customer.domain.accessibility.summary',
        visibilityKey: 'accessibility-root',
        children: [
            {
                id: 'accessibility-summary',
                label: 'Summary',
                route: 'base.customer.domain.accessibility.summary',
            },
            {
                id: 'accessibility-issues-by-type',
                label: 'Issues by Type',
                route: 'base.customer.domain.accessibility.issues-by-type',
                visibilityKey: 'accessibility-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.issues-by-type',
                    'base.customer.domain.accessibility.affected-pages',
                    'base.customer.domain.accessibility.affected-page-by-issue',
                ],
            },
            {
                id: 'accessibility-issues-by-page',
                label: 'Issues by Page',
                route: 'base.customer.domain.accessibility.issues-by-page',
                visibilityKey: 'accessibility-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.issues-by-page',
                    'base.customer.domain.accessibility.page-checks',
                    'base.customer.domain.accessibility.code-snippets-by-page',
                ],
            },
            {
                id: 'accessibility-pages',
                label: 'Pages with Failing Checks',
                route: 'base.customer.domain.accessibility.pages',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-checklist',
                label: 'Checklist',
                route: 'base.customer.domain.accessibility.issues',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-guidelines',
                label: 'Guidelines',
                route: 'base.customer.domain.accessibility.guidelines',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-ignored-pages',
                label: 'Pages with Ignored Checks',
                route: 'base.customer.domain.accessibility.ignored-pages',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-fast-track',
                label: 'Fast Track',
                route: 'base.customer.domain.accessibility.fast-track',
                visibilityKey: 'accessibility-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.fast-track',
                    'base.customer.domain.accessibility.code-snippet',
                    'base.customer.domain.accessibility.fast-track-check',
                ],
            },
            {
                id: 'accessibility-pdfs',
                label: 'PDFs',
                route: 'base.customer.domain.accessibility.pdfs',
                visibilityKey: 'accessibility-pdfs-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.pdfs',
                    'base.customer.domain.accessibility.pdfs-report',
                    'base.customer.domain.accessibility.pdfs-report-checks',
                ],
                children: [
                    {
                        id: 'accessibility-pdfs-internal',
                        label: 'Internal PDFs',
                        route: 'base.customer.domain.accessibility.pdfs',
                        routeParams: {
                            type: 'internal',
                            tab: null,
                            page: null,
                            page_size: null,
                            sort_by: null,
                            sort_dir: null,
                            search: null,
                        },
                        activeRoutes: [
                            'base.customer.domain.accessibility.pdfs-report',
                            'base.customer.domain.accessibility.pdfs-report-checks',
                        ],
                    },
                    {
                        id: 'accessibility-pdfs-external',
                        label: 'External PDFs',
                        route: 'base.customer.domain.accessibility.pdfs',
                        routeParams: {
                            type: 'external',
                            tab: null,
                            page: null,
                            page_size: null,
                            sort_by: null,
                            sort_dir: null,
                            search: null,
                        },
                    },
                ],
            },
            {
                id: 'accessibility-fast-track',
                label: 'Fast Track',
                route: 'base.customer.domain.accessibility.fasttrack',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-pdfs',
                label: 'PDFs',
                route: 'base.customer.domain.accessibility.internal-pdf',
                visibilityKey: 'accessibility-pdfs-legacy',
                activeRoutes: [
                    'base.customer.domain.accessibility.internal-pdf',
                    'base.customer.domain.accessibility.internal-pdf-accessibility',
                    'base.customer.domain.accessibility.external-pdf',
                ],
                children: [
                    {
                        id: 'accessibility-pdfs-internal',
                        label: 'Internal PDFs',
                        route: 'base.customer.domain.accessibility.internal-pdf',
                        activeRoutes: [
                            'base.customer.domain.accessibility.internal-pdf',
                            'base.customer.domain.accessibility.internal-pdf-accessibility',
                        ],
                    },
                    {
                        id: 'accessibility-pdfs-external',
                        label: 'External PDFs',
                        route: 'base.customer.domain.accessibility.external-pdf',
                    },
                ],
            },
        ],
    },
    {
        id: 'content-quality',
        label: 'Content Quality',
        icon: 'contentQuality',
        route: 'base.customer.domain.qa.summary',
        visibilityKey: 'quality-root',
        children: [
            {
                id: 'content-quality-summary',
                label: 'Summary',
                route: 'base.customer.domain.qa.summary',
                visibilityKey: 'quality-summary',
            },
            {
                id: 'content-quality-pages',
                label: 'Issues by Page',
                route: 'base.customer.domain.qa.pages',
                visibilityKey: 'quality-summary',
            },
            {
                id: 'content-quality-links',
                label: 'Broken Content',
                route: 'base.customer.domain.qa.broken.links',
                visibilityKey: 'quality-links',
                activeRoutes: [
                    'base.customer.domain.qa.broken.links',
                    'base.customer.domain.qa.broken.images',
                    'base.customer.domain.qa.broken.pages',
                    'base.customer.domain.qa.broken.sitemap',
                ],
                children: [
                    {
                        id: 'content-quality-links-pages',
                        label: 'Broken Content by Page',
                        route: 'base.customer.domain.qa.broken.pages',
                    },
                    {
                        id: 'content-quality-links-links',
                        label: 'Broken Links',
                        route: 'base.customer.domain.qa.broken.links',
                    },
                    {
                        id: 'content-quality-links-images',
                        label: 'Broken Images',
                        route: 'base.customer.domain.qa.broken.images',
                    },
                    {
                        id: 'content-quality-links-sitemap',
                        label: 'Broken Sitemap Links',
                        route: 'base.customer.domain.qa.broken.sitemap',
                    },
                ],
            },
            {
                id: 'content-quality-spellcheck',
                label: 'Misspellings',
                route: 'base.customer.domain.qa.spellcheck.index',
                visibilityKey: 'quality-spellcheck',
                activeRoutes: [
                    'base.customer.domain.qa.spellcheck.index',
                    'base.customer.domain.qa.spellcheck.pages',
                    'base.customer.domain.qa.spellcheck.potential',
                    'base.customer.domain.qa.spellcheck.confirmed',
                    'base.customer.domain.qa.spellcheck.dictionary',
                    'base.customer.domain.qa.spellcheck.ignored-misspellings',
                    'base.customer.domain.qa.spellcheck.qa-errors',
                    'base.customer.domain.qa.spellcheck.dashboard',
                ],
                children: [
                    {
                        id: 'content-quality-spellcheck-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.qa.spellcheck.dashboard',
                    },
                    {
                        id: 'content-quality-spellcheck-pages',
                        label: 'Misspellings by Page',
                        route: 'base.customer.domain.qa.spellcheck.pages',
                    },
                    {
                        id: 'content-quality-spellcheck-misspellings',
                        label: 'Misspellings',
                        route: 'base.customer.domain.qa.spellcheck.confirmed',
                    },
                    {
                        id: 'content-quality-spellcheck-potential',
                        label: 'Potential Misspellings',
                        route: 'base.customer.domain.qa.spellcheck.potential',
                    },
                    {
                        id: 'content-quality-spellcheck-dictionary',
                        label: 'Dictionary',
                        route: 'base.customer.domain.qa.spellcheck.dictionary',
                    },
                    {
                        id: 'content-quality-spellcheck-ignored',
                        label: 'Ignored Misspellings',
                        route: 'base.customer.domain.qa.spellcheck.ignored-misspellings',
                    },
                ],
            },
            {
                id: 'content-quality-readability',
                label: 'Readability',
                route: 'base.customer.domain.qa.readability.dashboard',
                visibilityKey: 'quality-readability',
                activeRoutes: [
                    'base.customer.domain.qa.readability.dashboard',
                    'base.customer.domain.qa.readability.checker',
                ],
                children: [
                    {
                        id: 'content-quality-readability-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.qa.readability.dashboard',
                    },
                    {
                        id: 'content-quality-readability-checker',
                        label: 'Readability Checker',
                        route: 'base.customer.domain.qa.readability.checker',
                    },
                ],
            },
            {
                id: 'content-quality-language',
                label: 'Language Validation',
                route: 'base.customer.domain.qa.languageMatch.summary',
                visibilityKey: 'quality-language',
                activeRoutes: [
                    'base.customer.domain.qa.languageMatch.summary',
                    'base.customer.domain.qa.languageMatch.pages',
                ],
                children: [
                    {
                        id: 'content-quality-language-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.qa.languageMatch.summary',
                    },
                    {
                        id: 'content-quality-language-pages',
                        label: 'Issues by Page',
                        route: 'base.customer.domain.qa.languageMatch.pages',
                    },
                ],
            },
            {
                id: 'content-quality-policies',
                label: 'Content Policies',
                route: 'base.customer.domain.policy.dashboard',
                visibilityKey: 'quality-policies',
                activeRoutes: [
                    'base.customer.domain.policy.dashboard',
                    'base.customer.domain.policy.pages',
                    'base.customer.domain.policy.policies',
                    'base.customer.domain.policy.ignored-pages',
                ],
                children: [
                    {
                        id: 'content-quality-policies-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.policy.dashboard',
                    },
                    {
                        id: 'content-quality-policies-pages',
                        label: 'Issues by Page',
                        route: 'base.customer.domain.policy.pages',
                    },
                    {
                        id: 'content-quality-policies-list',
                        label: 'Policies List',
                        route: 'base.customer.domain.policy.policies',
                    },
                    {
                        id: 'content-quality-policies-ignored',
                        label: 'Pages with Ignored Policies',
                        route: 'base.customer.domain.policy.ignored-pages',
                    },
                ],
            },
            {
                id: 'content-quality-pagecorrect',
                label: 'PageCorrect',
                route: 'base.customer.domain.page-fix.summary',
                visibilityKey: 'quality-pagecorrect',
                activeRoutes: [
                    'base.customer.domain.page-fix.summary',
                    'base.customer.domain.page-fix.index',
                    'base.customer.domain.page-fix.checks',
                ],
                children: [
                    {
                        id: 'content-quality-pagecorrect-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.page-fix.summary',
                    },
                    {
                        id: 'content-quality-pagecorrect-list-of-fixes',
                        label: 'List of Fixes',
                        route: 'base.customer.domain.page-fix.index',
                    },
                    {
                        id: 'content-quality-pagecorrect-accessibility-fixes',
                        label: 'Accessibility Fixes',
                        route: 'base.customer.domain.page-fix.checks',
                        visibilityKey: 'quality-pagecorrect-accessibility-fixes',
                    },
                ],
            },
        ],
    },
    {
        id: 'insights',
        label: 'Insights',
        icon: 'insights',
        route: 'base.customer.domain.statistics.summary',
        visibilityKey: 'insights-root',
        children: [
            {
                id: 'insights-site-analytics',
                label: 'Site Analytics',
                route: 'base.customer.domain.statistics.summary',
                visibilityKey: 'statistics-root',
                routeMatchers: ['base.customer.domain.statistics'],
                children: [
                    {
                        id: 'insights-site-analytics-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.statistics.summary',
                        activeRoutes: [
                            'base.customer.domain.statistics.summary',
                        ],
                    },
                    {
                        id: 'insights-site-analytics-visitors',
                        label: 'Visitors',
                        route: 'base.customer.domain.statistics.visitors.summary',
                        activeRoutes: [
                            'base.customer.domain.statistics.visitors.summary',
                            'base.customer.domain.statistics.visitors.locations',
                            'base.customer.domain.statistics.visitors.locations.index',
                            'base.customer.domain.statistics.visitors.locations.regions',
                            'base.customer.domain.statistics.visitors.locations.cities',
                            'base.customer.domain.statistics.visitors.language',
                            'base.customer.domain.statistics.visitors.organizations',
                            'base.customer.domain.statistics.visitors.devices',
                            'base.customer.domain.statistics.visitors.browsers',
                            'base.customer.domain.statistics.visitors.os',
                            'base.customer.domain.statistics.visitors.resolutions',
                            'base.customer.domain.statistics.visitors.log',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-visitors-summary',
                                label: 'Visitors Summary',
                                route: 'base.customer.domain.statistics.visitors.summary',
                            },
                            {
                                id: 'insights-site-analytics-visitors-locations',
                                label: 'Locations',
                                route: 'base.customer.domain.statistics.visitors.locations.index',
                                activeRoutes: [
                                    'base.customer.domain.statistics.visitors.locations.index',
                                    'base.customer.domain.statistics.visitors.locations.regions',
                                    'base.customer.domain.statistics.visitors.locations.cities',
                                ],
                            },
                            {
                                id: 'insights-site-analytics-visitors-language',
                                label: 'Languages',
                                route: 'base.customer.domain.statistics.visitors.language',
                            },
                            {
                                id: 'insights-site-analytics-visitors-organizations',
                                label: 'ISPs',
                                route: 'base.customer.domain.statistics.visitors.organizations',
                            },
                            {
                                id: 'insights-site-analytics-visitors-devices',
                                label: 'Devices',
                                route: 'base.customer.domain.statistics.visitors.devices',
                            },
                            {
                                id: 'insights-site-analytics-visitors-browsers',
                                label: 'Browsers',
                                route: 'base.customer.domain.statistics.visitors.browsers',
                            },
                            {
                                id: 'insights-site-analytics-visitors-os',
                                label: 'Operating Systems',
                                route: 'base.customer.domain.statistics.visitors.os',
                            },
                            {
                                id: 'insights-site-analytics-visitors-resolutions',
                                label: 'Screen Resolutions',
                                route: 'base.customer.domain.statistics.visitors.resolutions',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-analytics-content',
                        label: 'Content',
                        route: 'base.customer.domain.statistics.pages.mostPopular',
                        activeRoutes: [
                            'base.customer.domain.statistics.pages.mostPopular',
                            'base.customer.domain.statistics.pages.mostDownloads',
                            'base.customer.domain.statistics.pages.leastPopular',
                            'base.customer.domain.statistics.pages.withoutVisits',
                            'base.customer.domain.statistics.pages.documentsWithoutDownloads',
                            'base.customer.domain.statistics.pages.siteSearch',
                            'base.customer.domain.statistics.pages.events',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-content-most-popular',
                                label: 'Most Visited Pages',
                                route: 'base.customer.domain.statistics.pages.mostPopular',
                            },
                            {
                                id: 'insights-site-analytics-content-least-popular',
                                label: 'Least Visited Pages',
                                route: 'base.customer.domain.statistics.pages.leastPopular',
                            },
                            {
                                id: 'insights-site-analytics-content-most-downloads',
                                label: 'Most Downloads',
                                route: 'base.customer.domain.statistics.pages.mostDownloads',
                            },
                            {
                                id: 'insights-site-analytics-content-without-visits',
                                label: 'Pages with No Visits',
                                route: 'base.customer.domain.statistics.pages.withoutVisits',
                            },
                            {
                                id: 'insights-site-analytics-content-docs-without-downloads',
                                label: 'Documents with No Downloads',
                                route: 'base.customer.domain.statistics.pages.documentsWithoutDownloads',
                            },
                            {
                                id: 'insights-site-analytics-content-events',
                                label: 'Events',
                                route: 'base.customer.domain.statistics.pages.events',
                            },
                            {
                                id: 'insights-site-analytics-content-site-search',
                                label: 'Site Search',
                                route: 'base.customer.domain.statistics.pages.siteSearch',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-analytics-traffic-sources',
                        label: 'Traffic Sources',
                        route: 'base.customer.domain.statistics.sources.summary',
                        activeRoutes: [
                            'base.customer.domain.statistics.sources.summary',
                            'base.customer.domain.statistics.sources.searchEngines',
                            'base.customer.domain.statistics.sources.social',
                            'base.customer.domain.statistics.sources.domains',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-traffic-sources-summary',
                                label: 'Summary',
                                route: 'base.customer.domain.statistics.sources.summary',
                            },
                            {
                                id: 'insights-site-analytics-traffic-sources-search-engines',
                                label: 'Search Engines',
                                route: 'base.customer.domain.statistics.sources.searchEngines',
                            },
                            {
                                id: 'insights-site-analytics-traffic-sources-social-networks',
                                label: 'Social Networks',
                                route: 'base.customer.domain.statistics.sources.social',
                            },
                            {
                                id: 'insights-site-analytics-traffic-sources-referring-domains',
                                label: 'Referring Domains',
                                route: 'base.customer.domain.statistics.sources.domains',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-analytics-entry-exit',
                        label: 'Entry and Exit',
                        route: 'base.customer.domain.statistics.entryExit.entry',
                        activeRoutes: [
                            'base.customer.domain.statistics.entryExit.entry',
                            'base.customer.domain.statistics.entryExit.exit',
                            'base.customer.domain.statistics.entryExit.bounceRates',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-entry-exit-entry',
                                label: 'Entry Pages',
                                route: 'base.customer.domain.statistics.entryExit.entry',
                            },
                            {
                                id: 'insights-site-analytics-entry-exit-exit',
                                label: 'Exit Pages',
                                route: 'base.customer.domain.statistics.entryExit.exit',
                            },
                            {
                                id: 'insights-site-analytics-entry-exit-bounce-rates',
                                label: 'Bounce Rates',
                                route: 'base.customer.domain.statistics.entryExit.bounceRates',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'insights-scan-history',
                label: 'Scan History',
                // Nearest existing state fallback for legacy "Scan History" entry.
                route: 'base.customer.domain.changesHistory',
                visibilityKey: 'history-center',
                activeRoutes: ['base.customer.domain.changesHistory'],
            },
            {
                id: 'insights-site-inventory',
                label: 'Site Inventory',
                route: 'base.customer.domain.inventory.dashboard',
                visibilityKey: 'inventory-root',
                routeMatchers: ['base.customer.domain.inventory'],
                children: [
                    {
                        id: 'insights-site-inventory-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.inventory.dashboard',
                    },
                    {
                        id: 'insights-site-inventory-content',
                        label: 'Content',
                        route: 'base.customer.domain.inventory.content.pages',
                        activeRoutes: [
                            'base.customer.domain.inventory.content.pages',
                            'base.customer.domain.inventory.content.documents',
                            'base.customer.domain.inventory.content.images',
                            'base.customer.domain.inventory.content.inlineimages',
                            'base.customer.domain.inventory.content.links',
                        ],
                        children: [
                            {
                                id: 'insights-site-inventory-content-html-pages',
                                label: 'HTML Pages',
                                route: 'base.customer.domain.inventory.content.pages',
                            },
                            {
                                id: 'insights-site-inventory-content-documents',
                                label: 'Documents',
                                route: 'base.customer.domain.inventory.content.documents',
                            },
                            {
                                id: 'insights-site-inventory-content-images',
                                label: 'Images',
                                route: 'base.customer.domain.inventory.content.images',
                            },
                            {
                                id: 'insights-site-inventory-content-inline-images',
                                label: 'Inline Images',
                                route: 'base.customer.domain.inventory.content.inlineimages',
                            },
                            {
                                id: 'insights-site-inventory-content-links',
                                label: 'Links',
                                route: 'base.customer.domain.inventory.content.links',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-inventory-technical',
                        label: 'Technical',
                        route: 'base.customer.domain.inventory.content.form',
                        activeRoutes: [
                            'base.customer.domain.inventory.content.form',
                            'base.customer.domain.inventory.content.headlink',
                            'base.customer.domain.inventory.content.iframe',
                            'base.customer.domain.inventory.content.frame',
                            'base.customer.domain.inventory.content.css',
                            'base.customer.domain.inventory.content.js',
                        ],
                        children: [
                            {
                                id: 'insights-site-inventory-technical-forms',
                                label: 'Forms',
                                route: 'base.customer.domain.inventory.content.form',
                            },
                            {
                                id: 'insights-site-inventory-technical-headlinks',
                                label: 'Headlinks',
                                route: 'base.customer.domain.inventory.content.headlink',
                            },
                            {
                                id: 'insights-site-inventory-technical-iframes',
                                label: 'Iframes',
                                route: 'base.customer.domain.inventory.content.iframe',
                            },
                            {
                                id: 'insights-site-inventory-technical-frames',
                                label: 'Frames',
                                route: 'base.customer.domain.inventory.content.frame',
                            },
                            {
                                id: 'insights-site-inventory-technical-css',
                                label: 'CSS',
                                route: 'base.customer.domain.inventory.content.css',
                            },
                            {
                                id: 'insights-site-inventory-technical-javascript',
                                label: 'Javascript',
                                route: 'base.customer.domain.inventory.content.js',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-inventory-personal',
                        label: 'Data',
                        route: 'base.customer.domain.inventory.email',
                        activeRoutes: ['base.customer.domain.inventory.email'],
                        children: [
                            {
                                id: 'insights-site-inventory-personal-email',
                                label: 'Email Addresses',
                                route: 'base.customer.domain.inventory.email',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'site-performance',
        label: 'Site Performance',
        icon: 'sitePerformance',
        route: 'base.customer.domain.seo.summary',
        visibilityKey: 'site-performance-root',
        children: [
            {
                id: 'site-performance-seo',
                label: 'SEO Issues',
                route: 'base.customer.domain.seo.summary',
                visibilityKey: 'seo-root',
                activeRoutes: [
                    'base.customer.domain.seo.summary',
                    'base.customer.domain.seo.pages',
                    'base.customer.domain.seo.issues',
                ],
                children: [
                    {
                        id: 'site-performance-seo-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.seo.summary',
                    },
                    {
                        id: 'site-performance-seo-pages',
                        label: 'Issues by Page',
                        route: 'base.customer.domain.seo.pages',
                    },
                    {
                        id: 'site-performance-seo-checkpoints',
                        label: 'Issues by Type',
                        route: 'base.customer.domain.seo.issues',
                    },
                ],
            },
            {
                id: 'site-performance-scans',
                label: 'Performance Scans',
                route: 'base.customer.domain.page-performance.pages',
                visibilityKey: 'performance-root',
                activeRoutes: [
                    'base.customer.domain.page_performance',
                    'base.customer.domain.page-performance.pages',
                ],
            },
            {
                id: 'insights-uptime-monitoring',
                label: 'Uptime Monitoring',
                route: 'base.customer.domain.uptimes.index',
                visibilityKey: 'uptime-root',
                activeRoutes: [
                    'base.customer.domain.uptimes',
                    'base.customer.domain.uptimes.index',
                    'base.customer.domain.uptimes.show',
                ],
            },
        ],
    },
    {
        id: 'privacy',
        label: 'Privacy',
        icon: 'privacy',
        route: 'base.customer.domain.data-privacy.summary',
        visibilityKey: 'privacy-root',
        children: [
            {
                id: 'privacy-data',
                label: 'Data Privacy',
                route: 'base.customer.domain.data-privacy.summary',
                visibilityKey: 'data-privacy-core',
                activeRoutes: [
                    'base.customer.domain.data-privacy.summary',
                    'base.customer.domain.data-privacy.index',
                    'base.customer.domain.data-privacy.issues',
                    'base.customer.domain.data-privacy.checklist',
                    'base.customer.domain.data-privacy.domain-settings',
                ],
                children: [
                    {
                        id: 'privacy-data-summary',
                        label: 'Summary',
                        route: 'base.customer.domain.data-privacy.summary',
                    },
                    {
                        id: 'privacy-data-fast-track',
                        label: 'Fast Track',
                        dialogParams: {
                            body: PrivacyDialogsFastTrackComponent,
                            size: 'fw',
                        },
                    },
                    {
                        id: 'privacy-data-content',
                        label: 'Issues by Page',
                        route: 'base.customer.domain.data-privacy.index',
                    },
                    {
                        id: 'privacy-data-issues',
                        label: 'Issues by Type',
                        route: 'base.customer.domain.data-privacy.issues',
                    },
                    {
                        id: 'privacy-data-checklist',
                        label: 'Checklist',
                        route: 'base.customer.domain.data-privacy.checklist',
                    },
                ],
            },
            {
                id: 'privacy-consent-overview',
                label: 'Cookie Consent Manager',
                route: 'base.customer.domain.data-privacy.consent-overview',
                visibilityKey: 'consent-manager-root',
                activeRoutes: [
                    'base.customer.domain.data-privacy.consent-overview',
                    'base.customer.domain.data-privacy.consent-overview.cookie-acceptance-rate',
                    'base.customer.domain.data-privacy.consent-overview.consent-log',
                    'base.customer.domain.data-privacy.consent-overview.cookie-overview',
                ],
                children: [
                    {
                        id: 'privacy-consent-cookie-acceptance',
                        label: 'Acceptance Rates',
                        route: 'base.customer.domain.data-privacy.consent-overview.cookie-acceptance-rate',
                    },
                    {
                        id: 'privacy-consent-log',
                        label: 'Consent Log',
                        route: 'base.customer.domain.data-privacy.consent-overview.consent-log',
                    },
                    {
                        id: 'privacy-consent-cookie-overview',
                        label: 'Cookie Overview',
                        route: 'base.customer.domain.data-privacy.consent-overview.cookie-overview',
                        visibilityKey: 'consent-manager-cookie-overview',
                    },
                ],
            },
        ],
    },
];

export const ADMIN_SIDE_NAV_ITEMS: SideNavigationItem[] = [
    {
        id: 'admin-dashboard',
        label: 'Dashboard',
        icon: 'home',
        route: 'base.customer.admin.dashboard',
        activeRoutes: ['base.customer.admin.dashboard'],
    },
    {
        id: 'admin-user-management',
        label: 'User Management',
        icon: 'group',
        children: [
            {
                id: 'admin-users',
                label: 'Users',
                route: 'base.customer.admin.users',
                activeRoutes: [
                    'base.customer.admin.users',
                    'base.customer.admin.users.index',
                    'base.customer.admin.add-user',
                    'base.customer.admin.edit-user',
                ],
            },
            {
                id: 'admin-audit-log',
                label: 'Audit Log',
                route: 'base.customer.admin.audit-log',
                visibilityKey: 'admin-audit-log',
                activeRoutes: [
                    'base.customer.admin.audit-log',
                    'base.customer.admin.auditlog',
                    'base.customer.admin.auditlog.index',
                ],
            },
        ],
    },
    {
        id: 'admin-configuration',
        label: 'Configuration',
        icon: 'build',
        children: [
            {
                id: 'admin-labels',
                label: 'Labels',
                route: 'base.customer.admin.labels',
                visibilityKey: 'admin-labels',
                activeRoutes: [
                    'base.customer.admin.labels',
                    'base.customer.admin.labels.index',
                ],
            },
            {
                id: 'admin-performance-profiles',
                label: 'Performance Profiles',
                route: 'base.customer.admin.performance-profiles',
                visibilityKey: 'admin-page-performance',
                activeRoutes: [
                    'base.customer.admin.performance-profiles',
                    'base.customer.admin.performance-profile-form',
                ],
            },
            {
                id: 'admin-scan-exclusions',
                label: 'Scan Exclusions',
                route: 'base.customer.admin.scan-exclusions',
                visibilityKey: 'admin-source-code-excludes',
                activeRoutes: [
                    'base.customer.admin.scan_exclusions',
                    'base.customer.admin.add-scan-exclusions',
                    'base.customer.admin.edit-scan-exclusions',
                    'base.customer.admin.source_code_excludes',
                    'base.customer.admin.source-code-excludes.index',
                ],
            },
        ],
    },
    {
        id: 'admin-integrations',
        label: 'Integrations',
        icon: 'extension',
        children: [
            {
                id: 'admin-api-users',
                label: 'API Tokens',
                route: 'base.customer.admin.api-users',
                visibilityKey: 'admin-api-users',
                activeRoutes: [
                    'base.customer.admin.api_users',
                    'base.customer.admin.api-users.index',
                ],
            },
            {
                id: 'admin-jira-connector',
                label: 'Jira Connector',
                route: 'base.customer.admin.jira-connector',
                visibilityKey: 'admin-jira-connector',
                activeRoutes: ['base.customer.admin.jira-connector'],
            },
        ],
    },
];
